import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { PS } from 'app/model/ps';
import { RemoteAssessment } from 'app/model/remote-assessment';
import { Contact } from 'app/model/contact';
import { ContextService } from 'app/services/context.service';
import { CrmService } from 'app/services/crm.service';
import { ToastService } from 'app/services/toast.service';
import { NgWizardService } from 'ng-wizard';
import { NgBlockUI, BlockUI } from 'ng-block-ui';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';

interface SelectedServicesByLevel {
  levelId: number;
  levelLabel: string;
  services: PS[];
}

@Component({
  selector: 'app-confirm-selection',
  templateUrl: './confirm-selection.component.html',
  styleUrls: ['./confirm-selection.component.css']
})
export class ConfirmSelectionComponent implements OnInit {
  @BlockUI() blockUI: NgBlockUI;
  @ViewChild('psd18Modal', { static: true }) psd18Modal: TemplateRef<any>;

  remoteAssessment: RemoteAssessment;
  selectedServices: PS[] = [];
  displayedServices: PS[] = [];
  groupedSelectedServices: SelectedServicesByLevel[] = [];
  isDifferentLevel = false;
  psd18ModalMessage = '';
  private readonly eosAssessmentCreatedFromId = 806460000;
  private hasAutoTriggeredEosStartAssessment = false;
  // null = not yet decided; true = YES; false = NO
  userDecision: boolean | null = null;

  constructor(
    private ngWizardService: NgWizardService,
    private context: ContextService,
    private crmService: CrmService,
    private toastService: ToastService,
    private modalService: NgbModal
  ) {
    this.context.remoteAssessment.subscribe(ra => {
      this.remoteAssessment = ra;
      this.refreshSelectionView();
    });
  }

  ngOnInit(): void {
    // Re-run after all constructors have completed so ps.selected mutations
    // from Step 1's assessmentPS loop are guaranteed to be visible.
    this.refreshSelectionView();
  }

  previousStep() {
    this.ngWizardService.previous();
  }

  continueSameLevel() {
    const bypassAccreditedLevelValidation = this.isEosNoOutOfLevelAndNotPsd09a || this.isDifferentLevel;
    this.submitSelectedServices(this.selectedServices, false, bypassAccreditedLevelValidation);
  }

  selectYes() {
    this.userDecision = true;
  }

  selectNo() {
    this.userDecision = false;
  }

  proceedWithDecision() {
    if (this.userDecision === true) {
      this.submitSelectedServicesWithCleanup(this.selectedServices);
    } else if (this.userDecision === false) {
      this.ngWizardService.previous();
    }
  }


  private refreshSelectionView() {
    this.selectedServices = (this.remoteAssessment?.professionalServices || []).filter(ps => ps.selected === true);
    this.displayedServices = (this.remoteAssessment?.professionalServices || []).filter(ps =>
      ps.selected === true || (ps.originallySelected === true && ps.selected === false)
    );
    this.groupedSelectedServices = this.buildGroupedSelectedServices(this.displayedServices);
    this.isDifferentLevel = this.isDifferentLevelSelection(this.selectedServices);
    this.userDecision = null;
    this.tryAutoStartAssessmentForEosCreatedFrom();
  }

  private getEosAutoTriggerKey(): string {
    const assessmentId = this.remoteAssessment?.assessment?.id || '';
    return `EOS_START_ASSESSMENT_AUTO_TRIGGERED_${assessmentId}`;
  }

  private getEosAutoTriggerInFlightKey(): string {
    const assessmentId = this.remoteAssessment?.assessment?.id || '';
    return `EOS_START_ASSESSMENT_AUTO_TRIGGERING_${assessmentId}`;
  }

  private getEosCancelledNoOutOfLevelKey(): string {
    const assessmentId = this.remoteAssessment?.assessment?.id || '';
    return `EOS_CANCELLED_NO_OUT_OF_LEVEL_${assessmentId}`;
  }

  private tryAutoStartAssessmentForEosCreatedFrom(): void {
    if (!this.remoteAssessment || this.hasAutoTriggeredEosStartAssessment) {
      return;
    }

    const createdFromId = Number(this.remoteAssessment?.assessment?.assessmentcreatedfrom?.id);
    const isEosCreatedFrom = createdFromId === this.eosAssessmentCreatedFromId;
    const hasAnswers = (this.remoteAssessment?.remoteAnswer || []).length > 0;
    const hasSelectedServices = (this.selectedServices || []).length > 0;
    const alreadyTriggered = sessionStorage.getItem(this.getEosAutoTriggerKey()) === 'true';
    const autoTriggerInFlight = sessionStorage.getItem(this.getEosAutoTriggerInFlightKey()) === 'true';

    const shouldAutoTrigger = isEosCreatedFrom
      && !this.remoteAssessment?.readOnly
      && !!this.remoteAssessment?.isQuestionAvilable
      && !hasAnswers
      && hasSelectedServices
      && !autoTriggerInFlight
      && (!alreadyTriggered || this.remoteAssessment?.assessment?.isPSDLocked === true);

    if (!shouldAutoTrigger) {
      return;
    }

    this.hasAutoTriggeredEosStartAssessment = true;
    sessionStorage.setItem(this.getEosAutoTriggerKey(), 'true');
    sessionStorage.setItem(this.getEosAutoTriggerInFlightKey(), 'true');
    this.submitSelectedServices(this.selectedServices, true, true);
  }

  get isReviewAssessmentType(): boolean {
    const assessmentTypeId = String(this.remoteAssessment?.assessment?.assessmentType?.id || '');
    return assessmentTypeId === '806390003' || assessmentTypeId === '806390005';
  }

  isRemovedService(ps: PS): boolean {
    return ps.originallySelected === true && ps.selected !== true;
  }

  // Existing service for badge classification: present as ACTIVE in assessmentPS,
  // regardless of expandedService flag.
  private getExistingActiveServiceIdSet(): Set<string> {
    return new Set(
      (this.remoteAssessment?.assessmentPS || [])
        .filter(aps => String(aps?.status?.id ?? '') === '0')
        .map(aps => aps?.serviceName?.id)
        .filter(id => !!id)
    );
  }

  isExistingActiveService(ps: PS): boolean {
    return this.getExistingActiveServiceIdSet().has(ps?.id);
  }

  /** Builds the set of service IDs that are verified accredited from the backend payload:
   *  - status.id == "0" (active)
   *  - expandedService == false (original, not added during expansion)
   * This mirrors the same criteria used in Step 1 to set originallySelected,
   * but is computed independently in Step 2 as an ID-level verification layer. */
  private getAccreditedServiceIdSet(): Set<string> {
    return new Set(
      (this.remoteAssessment?.assessmentPS || [])
        .filter(aps => aps?.status?.id === '0' && !aps.expandedService)
        .map(aps => aps.serviceName?.id)
        .filter(id => !!id)
    );
  }

  /** Service already accredited: originallySelected AND ID verified in backend accredited list. */
  isVerifiedAccreditedService(ps: PS): boolean {
    return ps.originallySelected === true && this.getAccreditedServiceIdSet().has(ps.id);
  }

  /** Newly added service not matched: originallySelected set but ID not in accredited list. */
  isNewlyAddedUnmatched(ps: PS): boolean {
    return ps.originallySelected === true && !this.getAccreditedServiceIdSet().has(ps.id);
  }

  isOutOfLevelService(ps: PS): boolean {
    if (ps.selected !== true || ps.originallySelected === true || ps?.servicelevel?.id == null) {
      return false;
    }

    const pharmacyLevels = this.getPharmacyLevelsSet();
    return !pharmacyLevels.has(Number(ps.servicelevel.id));
  }

  private buildGroupedSelectedServices(services: PS[]): SelectedServicesByLevel[] {
    const grouped = new Map<number, PS[]>();

    services.forEach(ps => {
      const levelId = ps?.servicelevel?.id ?? 0;
      const existing = grouped.get(levelId) || [];
      existing.push(ps);
      grouped.set(levelId, existing);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([levelId, levelServices]) => ({
        levelId,
        levelLabel: this.formatLevelLabel(levelId, levelServices[0]?.servicelevelDisplayName),
        services: levelServices
      }));
  }

  get isExpansionAssessmentType(): boolean {
    const assessmentTypeId = String(this.remoteAssessment?.assessment?.assessmentType?.id || '');
    return assessmentTypeId === '806390002';
  }

  get assessmentProcessName(): string {
    const assessmentTypeId = String(this.remoteAssessment?.assessment?.assessmentType?.id || '');
    if (assessmentTypeId === '806390003') {
      return 'Transition Assessment';
    }
    if (assessmentTypeId === '806390005') {
      return 'Mid-Cycle Review';
    }
    return 'Assessment';
  }

  get isEosNoOutOfLevelAndNotPsd09a(): boolean {
    return this.isExpansionAssessmentType && !this.isDifferentLevel && !this.hasPsd09aLevels;
  }

  get addedOutOfLevelLevelLabelsText(): string {
    const labels = new Set<string>();

    (this.selectedServices || []).forEach(ps => {
      if (this.isOutOfLevelService(ps)) {
        const levelId = Number(ps?.servicelevel?.id ?? 0);
        labels.add(this.formatLevelLabel(levelId, ps?.servicelevelDisplayName));
      }
    });

    const sortedLabels = Array.from(labels).sort((a, b) => a.localeCompare(b));
    return sortedLabels.join(', ');
  }

  // PSD_09a: Returns true when at least one fully-emptied accredited level exists.
  get hasPsd09aLevels(): boolean {
    return this.getFullyRemovedAccreditedLevels().length > 0;
  }

  // PSD_09a: Formatted level label text for all fully-emptied accredited levels.
  get psd09aRemovedLevelLabelsText(): string {
    return this.getFullyRemovedAccreditedLevels()
      .map(entry => this.formatLevelLabel(entry.levelId, entry.displayName))
      .join(', ');
  }

  // Additional validation layer (Expansion only): at least one service must remain
  // selected under each already-accredited service level.
  get hasAccreditedLevelSelectionValidationError(): boolean {
    return this.getAccreditedLevelsWithNoSelectedServices().length > 0;
  }

  get accreditedLevelSelectionValidationLevelsText(): string {
    return this.getAccreditedLevelsWithNoSelectedServices()
      .map(entry => this.formatLevelLabel(entry.levelId, entry.displayName))
      .join(', ');
  }

  private getAccreditedLevelsWithNoSelectedServices(): { levelId: number; displayName: string }[] {
    if (!this.isExpansionAssessmentType) {
      return [];
    }

    const pharmacyLevels = this.getPharmacyLevelsSet();
    const allServices = this.remoteAssessment?.professionalServices || [];
    const result: { levelId: number; displayName: string }[] = [];

    pharmacyLevels.forEach(levelId => {
      const servicesAtLevel = allServices.filter(ps =>
        ps?.servicelevel?.id != null &&
        Number(ps.servicelevel.id) === levelId
      );

      if (servicesAtLevel.length === 0) {
        return;
      }

      const hasAnySelected = servicesAtLevel.some(ps => ps.selected === true);
      if (!hasAnySelected) {
        const displayName = servicesAtLevel[0]?.servicelevelDisplayName || '';
        result.push({ levelId, displayName });
      }
    });

    return result.sort((a, b) => a.levelId - b.levelId);
  }

  // PSD_12a: Returns true when at least one fully-emptied accredited level exists for TA/MCR.
  get hasPsd12aLevels(): boolean {
    if (!this.isReviewAssessmentType) {
      return false;
    }

    return this.getFullyRemovedAccreditedLevelsForReview().length > 0;
  }

  // PSD_12a: Formatted level label text for all fully-emptied accredited levels in TA/MCR.
  get psd12aRemovedLevelLabelsText(): string {
    return this.getFullyRemovedAccreditedLevelsForReview()
      .map(entry => this.formatLevelLabel(entry.levelId, entry.displayName))
      .join(', ');
  }

  // PSD_09a: Detects accredited levels where ALL active accredited services have been removed.
  // Triggers only for Expansion assessment type (806390002).
  // Scenario A (one removed, others remain) → empty result.
  // Scenario B (some removed, others remain) → empty result.
  // Scenario C (ALL removed from one accredited level) → returns that level.
  // Scenario D (ALL removed from multiple accredited levels) → returns all affected levels.
  private getFullyRemovedAccreditedLevels(): { levelId: number; displayName: string }[] {
    if (!this.isExpansionAssessmentType) {
      return [];
    }

    return this.calculateFullyRemovedAccreditedLevelsForPsd09a();
  }

  // PSD_12a: Same deselection calculation reused for TA/MCR.
  private getFullyRemovedAccreditedLevelsForReview(): { levelId: number; displayName: string }[] {
    if (!this.isReviewAssessmentType) {
      return [];
    }

    return this.calculateFullyRemovedAccreditedLevels();
  }

  // PSD_09a (Expansion): use active accredited services from assessmentPS regardless of expandedService.
  private calculateFullyRemovedAccreditedLevelsForPsd09a(): { levelId: number; displayName: string }[] {

    const pharmacyLevels = this.getPharmacyLevelsSet();
    const allServices = this.remoteAssessment?.professionalServices || [];
    const result: { levelId: number; displayName: string }[] = [];

    const activeAccreditedServiceIds = new Set(
      (this.remoteAssessment?.assessmentPS || [])
        .filter(aps => String(aps?.status?.id ?? '') === '0')
        .map(aps => aps?.serviceName?.id)
        .filter(id => !!id)
    );

    pharmacyLevels.forEach(levelId => {
      // All currently active accredited services at this pharmacy-accredited level.
      const accreditedAtLevel = allServices.filter(ps =>
        activeAccreditedServiceIds.has(ps?.id) &&
        ps?.servicelevel?.id != null &&
        Number(ps.servicelevel.id) === levelId
      );

      // Any selected service at this level that is NOT in the active accredited set.
      // If one exists, the level is not empty for PSD_09a warning purposes.
      const hasSelectedNonAccreditedAtLevel = allServices.some(ps =>
        ps.selected === true &&
        !activeAccreditedServiceIds.has(ps?.id) &&
        ps?.servicelevel?.id != null &&
        Number(ps.servicelevel.id) === levelId
      );

      if (
        accreditedAtLevel.length > 0 &&
        !accreditedAtLevel.some(ps => ps.selected === true) &&
        !hasSelectedNonAccreditedAtLevel
      ) {
        const displayName = accreditedAtLevel[0]?.servicelevelDisplayName || '';
        result.push({ levelId, displayName });
      }
    });

    return result.sort((a, b) => a.levelId - b.levelId);
  }

  // Shared full-deselection calculation across PSD_09a and PSD_12a.
  // Business rule: the warning fires only when the accredited level becomes completely empty —
  // meaning no selected service of any kind (original or newly added) remains at that level.
  private calculateFullyRemovedAccreditedLevels(): { levelId: number; displayName: string }[] {

    const pharmacyLevels = this.getPharmacyLevelsSet();
    const allServices = this.remoteAssessment?.professionalServices || [];
    const result: { levelId: number; displayName: string }[] = [];

    pharmacyLevels.forEach(levelId => {
      // All originally-accredited services at this pharmacy-accredited level.
      const originalAtLevel = allServices.filter(ps =>
        ps.originallySelected === true &&
        ps?.servicelevel?.id != null &&
        Number(ps.servicelevel.id) === levelId
      );

      // Any newly added service (not originally accredited) still selected at this level.
      // If one exists, the level is not empty — the warning must be suppressed.
      const hasNewServiceAtLevel = allServices.some(ps =>
        ps.selected === true &&
        ps.originallySelected !== true &&
        ps?.servicelevel?.id != null &&
        Number(ps.servicelevel.id) === levelId
      );

      // Level is fully removed only when:
      // 1. Originally-accredited services existed at this level, AND
      // 2. None of those original services remain selected, AND
      // 3. No newly added service fills the gap at the same level.
      if (
        originalAtLevel.length > 0 &&
        !originalAtLevel.some(ps => ps.selected === true) &&
        !hasNewServiceAtLevel
      ) {
        const displayName = originalAtLevel[0]?.servicelevelDisplayName || '';
        result.push({ levelId, displayName });
      }
    });

    // Sort by levelId for consistent display ordering
    return result.sort((a, b) => a.levelId - b.levelId);
  }

  
  private getSelectedLevelsSet(services: PS[]): Set<number> {
    return new Set(
      services
        .filter(ps => ps?.servicelevel?.id != null)
        .map(ps => Number(ps.servicelevel.id))
        .filter(levelId => !isNaN(levelId))
    );
  }

  private getPharmacyLevelsSet(): Set<number> {
    return new Set(
      (this.remoteAssessment?.pharmacyDetails?.servicelevel || [])
        .filter(level => level?.value != null)
        .map(level => Number(level.value))
        .filter(value => !isNaN(value))
    );
  }

  private isDifferentLevelSelection(services: PS[]): boolean {
    const selectedLevels = this.getSelectedLevelsSet(services);
    const pharmacyLevels = this.getPharmacyLevelsSet();
    let hasDifferentLevel = false;

    Array.from(selectedLevels).forEach(level => {
      if (!pharmacyLevels.has(level)) {
        hasDifferentLevel = true;
      }
    });

    return hasDifferentLevel;
  }

  private getSelectedOutOfLevelServices(selectedServices: PS[]): PS[] {
    const pharmacyLevels = this.getPharmacyLevelsSet();
    return selectedServices.filter(ps => {
      return ps.selected === true && ps?.servicelevel?.id != null && !pharmacyLevels.has(Number(ps.servicelevel.id));
    });
  }

  private getUniqueOutOfLevelNumbers(outOfLevelServices: PS[]): number[] {
    return Array.from(new Set(
      outOfLevelServices
        .map(ps => Number(ps?.servicelevel?.id))
        .filter(levelId => !isNaN(levelId))
        .map(levelId => this.mapServiceLevelToDisplayNumber(levelId))
    )).sort((a, b) => a - b);
  }

  private submitSelectedServices(selectedServices: PS[], suppressNavigation = false, bypassAccreditedLevelValidation = false) {
    if (!bypassAccreditedLevelValidation && this.hasAccreditedLevelSelectionValidationError) {
      return;
    }

    const contact = new Contact();
    contact.id = this.remoteAssessment.contactLookup.id;

    const selectedPayload = selectedServices.filter(ps => ps.selected === true);
    const outOfLevelServices = this.getSelectedOutOfLevelServices(selectedPayload);
    const levels = this.getUniqueOutOfLevelNumbers(outOfLevelServices);
    const hasOutOfLevelSelection = outOfLevelServices.length > 0;
    const isEosCancelledNoOutOfLevel = this.isExpansionAssessmentType && !hasOutOfLevelSelection && !this.hasPsd09aLevels;
    const loaderMessage = isEosCancelledNoOutOfLevel ? 'Processing your request...' : 'Generating your pharmacy customised checklist ';
    this.blockUI.start(loaderMessage);
    const isEosAutoStartPath = suppressNavigation === true && bypassAccreditedLevelValidation === true;
    const clearAutoStartInFlightFlag = () => {
      if (isEosAutoStartPath) {
        sessionStorage.removeItem(this.getEosAutoTriggerInFlightKey());
      }
    };
    this.crmService.UpdateProfessionalServices(
      this.remoteAssessment.assessment,
      selectedPayload,
      contact,
      undefined,
      levels
    ).pipe(
      finalize(() => {
        clearAutoStartInFlightFlag();
        this.blockUI.stop();
      })
    ).subscribe(
      r => {
        if (!r.result) {
          this.toastService.showDanger(r.message);
          this.remoteAssessment.readOnly = true;
          this.context.updateContextRA(this.remoteAssessment);
          if (!suppressNavigation) {
            this.ngWizardService.next();
          }
        } else {
          this.remoteAssessment.assessment.isPSDLocked = true;

          // Set PSD15 flag from the actual selected payload for this path.
          // EoS is required only for Expansion assessments with out-of-level selections.
          const isEosRequired = this.isExpansionAssessmentType && hasOutOfLevelSelection;
          sessionStorage.setItem('PSD15_EOS_REQUIRED', isEosRequired ? 'true' : 'false');

          // EOS cancellation scenario: EOS with no out-of-level services and no PSD_09a block.
          // Backend handles cancellation when Levels is empty.
          const isEosCancelledNoOutOfLevel = this.isExpansionAssessmentType && !hasOutOfLevelSelection && !this.hasPsd09aLevels;
          const eosCancelledKey = this.getEosCancelledNoOutOfLevelKey();
          sessionStorage.setItem(eosCancelledKey, isEosCancelledNoOutOfLevel ? 'true' : 'false');
          if (isEosCancelledNoOutOfLevel) {
            localStorage.setItem(eosCancelledKey, 'true');
          } else {
            localStorage.removeItem(eosCancelledKey);
          }

          if (this.remoteAssessment.isQuestionAvilable) {
            this.toastService.showSuccess('You professional service declaration has been accepted');
            this.remoteAssessment.remoteAnswer = r.remoteAnswers;
            this.remoteAssessment.isAssessmentStartDateContain = !!r.isAssessmentStartDateContain;
            this.context.updateContextRA(this.remoteAssessment);
            if (!suppressNavigation) {
              if (this.isExpansionAssessmentType && !isEosRequired) {
                this.ngWizardService.show(3);
              } else {
                this.ngWizardService.next();
              }
            }
          } else {
            this.toastService.showDanger(this.remoteAssessment.isQuestionAvilableMSG);
          }
        }
      },
      () => {
        this.toastService.showDanger('Error while generating your pharmacy customised checklist. Please try again.');
      }
    );
  }

  // Used for YES path: calls API then deselects higher-level services from UI state after success
    private submitSelectedServicesWithCleanup(selectedServices: PS[]) {
    const contact = new Contact();
    contact.id = this.remoteAssessment.contactLookup.id;

    this.blockUI.start('Generating your pharmacy customised checklist ');
    const selectedPayload = selectedServices.filter(ps => ps.selected === true);
    const pharmacyLevels = this.getPharmacyLevelsSet();
    const outOfLevelServices = this.getSelectedOutOfLevelServices(selectedPayload);
    const levels = this.getUniqueOutOfLevelNumbers(outOfLevelServices);

    const psSub = this.crmService.UpdateProfessionalServices(
      this.remoteAssessment.assessment,
      selectedPayload,
      contact,
      outOfLevelServices,
      levels
    ).subscribe(r => {
      if (!r.result) {
        this.toastService.showDanger(r.message);
        this.remoteAssessment.readOnly = true;
        this.context.updateContextRA(this.remoteAssessment);
        this.ngWizardService.next();
      } else {
        this.remoteAssessment.assessment.isPSDLocked = true;

        // Capture PSD15 outcome BEFORE the cleanup deselection loop mutates ps.selected.
        // outOfLevelServices is already computed above and contains the out-of-level set.
        sessionStorage.setItem('PSD15_EOS_REQUIRED', outOfLevelServices.length > 0 ? 'true' : 'false');

        // Remove additional-level services from current assessment UI state after success
        this.remoteAssessment.professionalServices.forEach(ps => {
          if (ps.selected === true && ps?.servicelevel?.id != null && !pharmacyLevels.has(Number(ps.servicelevel.id))) {
            ps.selected = false;
          }
        });

        if (this.remoteAssessment.isQuestionAvilable) {
          this.toastService.showSuccess('You professional service declaration has been accepted');
          const continueFlow = () => {
            this.remoteAssessment.remoteAnswer = r.remoteAnswers;
            this.remoteAssessment.isAssessmentStartDateContain = !!r.isAssessmentStartDateContain;
            this.context.updateContextRA(this.remoteAssessment);
            this.ngWizardService.next();
          };

          if (this.isReviewAssessmentType && this.isDifferentLevel) {
            const processName = this.assessmentProcessName;
            this.openPsd18Modal(
              `Submission of the Professional Services Declaration Form has generated an Expansion of Services assessment. Please check your email for next steps on how to access this assessment. You will now be directed to complete the ${processName}.`,
              continueFlow
            );
          } else {
            continueFlow();
          }
        } else {
          this.toastService.showDanger(this.remoteAssessment.isQuestionAvilableMSG);
        }
      }

      this.blockUI.stop();
      psSub.unsubscribe();
    });
  }

  private openPsd18Modal(message: string, onContinue: () => void): void {
    this.psd18ModalMessage = message;
    const modalRef = this.modalService.open(this.psd18Modal, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      windowClass: 'qspp-psd18-modal-window'
    });

    modalRef.result.then(
      () => onContinue(),
      () => {}
    );
  }

  private formatLevelLabel(levelId: number, displayName?: string): string {
    if (!(levelId > 0)) {
      return displayName || 'Level Unspecified';
    }

    const rawName = (displayName || '').trim();
    const nameWithoutSuffix = rawName.replace(/\s*\(Level\s*\d+\)\s*$/i, '');
    const nameWithoutPrefix = nameWithoutSuffix.replace(/^Level\s*\d+\s*[–-]\s*/i, '');
    const normalizedName = (nameWithoutPrefix || rawName).trim();

    return normalizedName || rawName;
  }

  private mapServiceLevelToDisplayNumber(levelId: number): number {
    const levelMap: Record<number, number> = {
      806460000: 1,
      806460001: 2,
      806460002: 3,
      806460003: 4
    };

    return levelMap[levelId] ?? levelId;
  }
}
